import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Loader2,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { ProviderRadio } from './email/ProviderRadio';
import { TestEmailCard } from './email/TestEmailCard';
import { SmtpForm } from './email/SmtpForm';
import { SesForm } from './email/SesForm';
import { useAdminOtpGuard } from './useAdminOtpGuard';

const EMPTY_SMTP = {
  host: '', port: 587, username: '', password: '', showPwd: false,
  fromEmail: '', fromName: '', useTls: true,
};
const EMPTY_SES = {
  accessKey: '', secret: '', showSecret: false, region: 'us-east-1',
  fromEmail: '', fromName: '',
};

export function AdminEmailIntegrationsPage() {
  const { admin, loading } = useAdminAuth();
  const [config, setConfig] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingSes, setSavingSes] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [sendingTest, setSendingTest] = useState(null); // 'gmail' | 'smtp' | 'ses'
  const [testEmail, setTestEmail] = useState('');

  // Form state lives here — child forms are pure presentational.
  const [smtpForm, setSmtpForm] = useState(EMPTY_SMTP);
  const [sesForm, setSesForm] = useState(EMPTY_SES);

  // Email-only OTP guard (Part C).
  const { guard, dialog: otpDialog } = useAdminOtpGuard();

  const setSmtp = useCallback((k, v) => setSmtpForm((f) => ({ ...f, [k]: v })), []);
  const setSes = useCallback((k, v) => setSesForm((f) => ({ ...f, [k]: v })), []);

  const refresh = async () => {
    try {
      const r = await adminApi.get('/admin/email-integrations');
      setConfig(r.data);
      const s = r.data.smtp;
      if (s) {
        setSmtpForm((f) => ({
          ...f,
          host: s.host || '',
          port: s.port || 587,
          username: s.username || '',
          fromEmail: s.from_email || '',
          fromName: s.from_name || '',
          useTls: s.use_tls,
        }));
      }
      const e = r.data.ses;
      if (e) {
        setSesForm((f) => ({
          ...f,
          accessKey: e.access_key_id || '',
          region: e.region || 'us-east-1',
          fromEmail: e.from_email || '',
          fromName: e.from_name || '',
        }));
      }
    } catch {
      toast.error('Could not load email integrations');
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if (admin) refresh();
  }, [admin]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const saveSmtp = async (e) => {
    e.preventDefault();
    if (!smtpForm.host.trim() || !smtpForm.username.trim() || !smtpForm.fromEmail.trim()) {
      toast.error('Host, username and from-email are required');
      return;
    }
    await guard({
      requestOtpUrl: '/admin/email-integrations/request-otp',
      title: 'Confirm SMTP save',
      description: 'Saving SMTP credentials needs Email OTP verification.',
      performSave: async (headers) => {
        setSavingSmtp(true);
        try {
          await adminApi.put('/admin/email-integrations/smtp', {
            host: smtpForm.host.trim(),
            port: Number(smtpForm.port),
            username: smtpForm.username.trim(),
            password: smtpForm.password.trim() || null,
            from_email: smtpForm.fromEmail.trim(),
            from_name: smtpForm.fromName.trim() || null,
            use_tls: smtpForm.useTls,
          }, { headers });
          toast.success('SMTP configuration saved');
          setSmtp('password', '');
          await refresh();
        } finally {
          setSavingSmtp(false);
        }
      },
    });
  };

  const saveSes = async (e) => {
    e.preventDefault();
    if (!sesForm.accessKey.trim() || !sesForm.fromEmail.trim() || !sesForm.region.trim()) {
      toast.error('Access key, region and from-email are required');
      return;
    }
    await guard({
      requestOtpUrl: '/admin/email-integrations/request-otp',
      title: 'Confirm AWS SES save',
      description: 'Saving AWS SES credentials needs Email OTP verification.',
      performSave: async (headers) => {
        setSavingSes(true);
        try {
          await adminApi.put('/admin/email-integrations/ses', {
            access_key_id: sesForm.accessKey.trim(),
            secret_access_key: sesForm.secret.trim() || null,
            region: sesForm.region.trim(),
            from_email: sesForm.fromEmail.trim(),
            from_name: sesForm.fromName.trim() || null,
          }, { headers });
          toast.success('AWS SES configuration saved');
          setSes('secret', '');
          await refresh();
        } finally {
          setSavingSes(false);
        }
      },
    });
  };

  const updateProvider = async (scope, provider) => {
    await guard({
      requestOtpUrl: '/admin/email-integrations/request-otp',
      title: 'Confirm provider change',
      description: `Switching the ${scope} email provider needs Email OTP verification.`,
      performSave: async (headers) => {
        setSavingProvider(true);
        try {
          await adminApi.put('/admin/email-integrations/provider', { [scope]: provider }, { headers });
          toast.success(`${scope === 'system' ? 'System' : 'Customer'} provider set to ${provider.toUpperCase()}`);
          await refresh();
        } finally {
          setSavingProvider(false);
        }
      },
    });
  };

  const sendTest = async (provider) => {
    if (!testEmail.trim() || !/^.+@.+\..+$/.test(testEmail.trim())) {
      toast.error('Please enter a valid recipient email above');
      return;
    }
    setSendingTest(provider);
    try {
      await adminApi.post('/admin/email-integrations/test', {
        provider,
        recipient: testEmail.trim(),
      });
      toast.success(`Test email sent via ${provider.toUpperCase()}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Test email failed');
    } finally {
      setSendingTest(null);
    }
  };

  const smtpReady = !!config?.smtp?.is_password_configured;
  const sesReady = !!config?.ses?.is_secret_configured;

  return (
    <div
      data-testid="admin-email-page"
      className="min-h-screen"
      style={{
        background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link
            to="/admin"
            className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800"
            data-testid="admin-email-back"
          >
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Mail className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Email Integrations</div>
            <div className="text-xs text-slate-400">
              Configure Custom SMTP or AWS SES for outbound email delivery
            </div>
          </div>
          <Link
            to="/admin/email-templates"
            data-testid="admin-email-view-templates"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition"
          >
            <FileText className="w-3.5 h-3.5" /> Edit Templates
          </Link>
          <Link
            to="/admin/email-logs"
            data-testid="admin-email-view-logs"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition"
          >
            <FileText className="w-3.5 h-3.5" /> Delivery Logs
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {fetchLoading ? (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Provider Selection */}
            <section
              data-testid="provider-selection"
              className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-4"
            >
              <div>
                <h2 className="text-white font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-400" /> Active providers
                </h2>
                <p className="text-xs text-slate-400 mt-1">
                  Pick which provider sends each kind of email. Defaults to Gmail (env vars) when nothing else is configured.
                </p>
              </div>
              <ProviderRadio
                scope="system"
                scopeLabel="System emails (OTPs / login)"
                current={config?.scopes?.system || 'gmail'}
                onChange={updateProvider}
                disabled={savingProvider}
                smtpReady={smtpReady}
                sesReady={sesReady}
              />
              <ProviderRadio
                scope="customer"
                scopeLabel="Customer emails (license codes / team invites)"
                current={config?.scopes?.customer || 'gmail'}
                onChange={updateProvider}
                disabled={savingProvider}
                smtpReady={smtpReady}
                sesReady={sesReady}
              />
            </section>

            <TestEmailCard
              testEmail={testEmail}
              setTestEmail={setTestEmail}
              sendingTest={sendingTest}
              onSendTest={sendTest}
              smtpReady={smtpReady}
              sesReady={sesReady}
            />

            <SmtpForm
              form={smtpForm}
              set={setSmtp}
              ready={smtpReady}
              saving={savingSmtp}
              onSubmit={saveSmtp}
            />

            <SesForm
              form={sesForm}
              set={setSes}
              ready={sesReady}
              saving={savingSes}
              onSubmit={saveSes}
            />
          </>
        )}
      </main>
      {otpDialog}
    </div>
  );
}
